import catalogue from 'content/stocks.catalogue.json'

/**
 * The thirteen tokenized equities Coinbase issues on Base, as data.
 *
 * It is a JSON file rather than a TypeScript module for one reason: the build
 * script that writes the prerendered HTML for `/stocks` and every
 * `/stocks/:slug` reads the same file. A page and the HTML a crawler is served
 * cannot then disagree about which companies exist or what they are called,
 * which is the failure a second hand-kept list produces within a month.
 *
 * Nothing here is a price. Every number a visitor sees is read from the chain
 * at the moment they look; this file only says what to read and where.
 */

export interface Stock {
  /** URL segment: the underlying ticker in lowercase — `/stocks/aapl`. */
  slug: string
  /** The token's own symbol, which carries the issuer's `c` suffix. */
  symbol: string
  /** The underlying equity's ticker, which is what people search for. */
  ticker: string
  name: string
  sector: string
  /** Lowercase. B20 metadata is mutable, so the address is the identity. */
  address: string
  decimals: number
  /** Chainlink V3 aggregator proxy on Base, 8 decimals. */
  feed: string
  icon: string
  about: string
}

export interface StockIssuer {
  name: string
  jurisdiction: string
  registry: string
}

export const STOCKS: Stock[] = catalogue.stocks
export const STOCK_ISSUER: StockIssuer = catalogue.issuer
/** Everything is quoted against USDC on Base — that is where the pools are. */
export const STOCK_QUOTE = catalogue.quote

export function stockBySlug(slug: string | undefined): Stock | null {
  if (!slug) return null
  const wanted = slug.toLowerCase()
  return STOCKS.find((s) => s.slug === wanted) ?? null
}

export function stockByAddress(address: string | undefined): Stock | null {
  if (!address) return null
  const wanted = address.toLowerCase()
  return STOCKS.find((s) => s.address === wanted) ?? null
}

/**
 * Whether an address is one of the issuer's tokens. Used by the swap screen to
 * label a token it would otherwise only know as an ERC-20, and deliberately an
 * address check — a symbol can be changed by the issuer with one call.
 */
export function isTokenizedStock(address: string | undefined): boolean {
  return stockByAddress(address) !== null
}
